import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LinkedInReviewContent } from "@/components/linkedin/LinkedInReviewContent";
import { LinkedInRevisaoComPerfil } from "@/components/linkedin/LinkedInRevisaoComPerfil";
import { linkedInApi } from "@/services/linkedin-api";

export default function LinkedInReviewPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const {
    data: job,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["linkedin-job", jobId],
    queryFn: () => linkedInApi.getJob(jobId!),
    enabled: !!jobId,
  });

  // Volta pra tela de onde veio (lote ou listagem). Sem histórico dentro do
  // app (link direto, refresh), cai na listagem.
  const goBack = () => {
    const historyState = window.history.state as { idx?: number } | null;
    if ((historyState?.idx ?? 0) > 0) navigate(-1);
    else navigate("/linkedin");
  };

  // Só review_ready aceita aplicar; nos demais status com extração (applied,
  // no_changes) a mesma tela vira visualização completa do que a IA extraiu,
  // com o diff e a proveniência do apply.
  const readOnly = !!job && job.status !== "review_ready";

  return (
    <div className="container mx-auto max-w-4xl p-6 space-y-4 xl:max-w-7xl">
      <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Button>

      <div>
        <h1 className="text-2xl font-bold">
          {readOnly ? "Extração do LinkedIn" : "Revisar upload de LinkedIn"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {readOnly
            ? "Dados extraídos pela IA e as mudanças em relação à versão anterior."
            : "Confira os dados extraídos e aplique ao perfil do freelancer."}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando...
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Não foi possível carregar este job. {(error as Error)?.message}
          </CardContent>
        </Card>
      ) : !job?.extracted_json ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Este job ainda não tem dados extraídos para mostrar
            {job ? ` (status: ${job.status}).` : "."}
          </CardContent>
        </Card>
      ) : (
        <LinkedInRevisaoComPerfil job={job}>
          <Card>
            <CardContent className="pt-6">
              <LinkedInReviewContent job={job} onClose={goBack} readOnly={readOnly} />
            </CardContent>
          </Card>
        </LinkedInRevisaoComPerfil>
      )}
    </div>
  );
}
